import type { EventAttendeeAdmin } from "@satyrsmc/shared/dto/admin/event";
import { PaymentStatus, PinLadder, TravelMode } from "@satyrsmc/shared/lib/enums";

function escapeCsvValue(val: string): string {
  if (val.includes(",") || val.includes('"') || val.includes("\n")) {
    return `"${val.replace(/"/g, '""')}"`;
  }
  return val;
}

/** Naive "last token of the display name is the surname" heuristic, same as
 * used for member vCard export — there are no separate first/last name fields
 * to sort on. */
function lastNameKey(name: string): string {
  const parts = name.trim().split(/\s+/);
  return (parts.length > 1 ? parts[parts.length - 1] : name).toLowerCase();
}

/** Same fallback used in the edit form: guess the pin ladder from travel mode
 * when nothing has been explicitly saved yet. */
function guessPinLadder(travelingBy: TravelMode | null | undefined): PinLadder | null {
  if (travelingBy == null) return null;
  return travelingBy === TravelMode.Motorcycle ? PinLadder.Biker : PinLadder.Rider;
}

function pinLadderLabel(
  pinLadder: PinLadder | null | undefined,
  travelingBy: TravelMode | null | undefined,
): string {
  const resolved = pinLadder ?? guessPinLadder(travelingBy);
  switch (resolved) {
    case PinLadder.Biker:
      return "Biker";
    case PinLadder.Rider:
      return "Rider";
    default:
      return "None";
  }
}

function paymentAmountLabel(a: EventAttendeeAdmin): string {
  const amount = a.payment_amount_cents != null ? (a.payment_amount_cents / 100).toFixed(2) : null;
  switch (a.payment_status) {
    case PaymentStatus.Pending:
      return `Owed $${amount ?? "0.00"}`;
    case PaymentStatus.Confirmed:
      return amount != null ? `Paid $${amount}` : "Paid";
    case PaymentStatus.Refunded:
      return amount != null ? `Refunded $${amount}` : "Refunded";
    case PaymentStatus.RefundRequested:
      return amount != null ? `Refund Requested $${amount}` : "Refund Requested";
    default:
      return "N/A";
  }
}

/** Export the given attendees as CSV, sorted alphabetically by last name. Pass
 * whatever rows are currently visible in the table (after search/filters) so the
 * download always matches what's on screen. */
export function downloadAttendeesCsv(
  attendees: EventAttendeeAdmin[],
  eventName?: string | null,
): void {
  const withName = attendees.map((a) => ({
    attendee: a,
    name: a.member?.name ?? a.contact?.display_name ?? "",
  }));
  withName.sort((x, y) => lastNameKey(x.name).localeCompare(lastNameKey(y.name)));

  const headers = [
    "Name",
    "Payment Amount",
    "Biker",
    "Waiver",
    "Pin Header",
    "Pin Ladder",
    "T-shirt",
    "Check-In",
    "Comments",
  ];
  const rows = withName.map(({ attendee: a, name }) => {
    const cd = a.campout_details;
    return [
      name,
      paymentAmountLabel(a),
      cd?.travelingBy === TravelMode.Motorcycle ? "Yes" : "No",
      a.waiver_signed ? "Yes" : "No",
      cd?.pinHeader ? "Yes" : "No",
      pinLadderLabel(cd?.pinLadder, cd?.travelingBy),
      cd?.tshirtSize ?? "",
      a.check_in_at ? "Yes" : "",
      cd?.club || (a.is_member ? "Member" : ""),
    ]
      .map(String)
      .map(escapeCsvValue);
  });

  const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const slug = eventName
    ? eventName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "")
    : "";
  a.download = slug ? `attendees-${slug}.csv` : "attendees.csv";
  a.click();
  URL.revokeObjectURL(url);
}
